import { useBoolean } from 'ahooks';
import { keyBy } from 'lodash';
import React, { FC, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';
import {
  CustomCardWidget,
  CustomChartWidget,
  WidgetType,
} from 'api/dashboard/widget';
import { Button } from 'components/atoms/button';
import { Form } from 'components/atoms/form';
import { PageLoading } from 'components/atoms/loading';
import { NotFound } from 'components/atoms/result';
import { CustomChartFormValues } from 'components/organisms/dashboard/components/custom-chart';
import { useDashboardInfo } from 'components/organisms/dashboard/useDashboardInfo';
import { ButtonGroup } from 'components/organisms/task-pane/task-detail/header/add-time-entry';
import { excludedAllTasksFields } from 'components/organisms/tasks/constants';
import {
  useFieldsAsDefaultGroupedFields,
  useFieldsAsGroupedFields,
} from 'components/pages/all-tasks/use-fields-as-grouped-fields';
import { getApiGroupedFilterTransformer } from 'components/pages/api-grouped-filter-transformer';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { RoutePaths } from 'constant';
import { RootState } from 'data';
import { useTranslation } from 'hooks/use-translation';
import { useQueryJiraConfig } from 'queries/jira-integration';
import { useEditWidgetMutation, useWidgetQuery } from 'queries/widgets';
import { FieldsStore, TaskField } from 'stores/fields';
import { excludedProjectFieldsForFilter } from 'utils/project-fields/constants';
import { useProjectFieldsAsFilterableFields } from 'utils/project-fields/use-project-fields-as-filterable-fields';
import { useGetDashboardUrlWithSearch } from 'utils/urls';
import { DashBoardDrawer, PrimaryTitle, SecondaryTitle } from './drawer';
import { EditCustomNumberWidget } from './edit-custom-card-widget';
import { EditCustomChartWidget } from './edit-custom-chart-widget';
import { EditDefaultWidget } from './edit-default-widget';
import { useWidgetTypeToName } from './step-one';
import { FormValues } from './step-two';

interface Params {
  dashboardId: string;
  widgetId: string;
}

export const EditWidgetPage: FC = () => {
  const { widgetId } = useParams<Params>();
  const widgetTypeToName = useWidgetTypeToName();
  const [visible, { setFalse }] = useBoolean(true);
  const { fields: allFields } = useProjectFieldsAsFilterableFields();

  const fieldsStore = useSelector(({ fields }: RootState) => fields);
  const { data: jiraIntegrationConfig } = useQueryJiraConfig();
  const getDashboardUrl = useGetDashboardUrlWithSearch();

  const excludedFields = useMemo(() => {
    // remove jiraIntegration from excluded fields ONLY when the integration is active
    if (jiraIntegrationConfig?.length) {
      return excludedAllTasksFields.filter(
        (field) => field !== 'jiraIntegration',
      );
    }

    return excludedAllTasksFields;
  }, [jiraIntegrationConfig]);
  const taskFields: TaskField[] = useMemo(
    () =>
      FieldsStore.enabledTaskFields(fieldsStore).filter(
        ({ fieldName }) => !excludedFields.includes(fieldName),
      ),
    [fieldsStore, excludedFields],
  );

  const projectFields = allFields.filter(
    (x) => !excludedProjectFieldsForFilter.includes(x.fieldName),
  );
  const projectFieldsLookupByName = useMemo(
    () => keyBy(projectFields, 'fieldName'),
    [projectFields],
  );
  const taskFieldsLookupByName = useMemo(
    () => keyBy(taskFields, 'fieldName'),
    [taskFields],
  );
  const groups: GroupFieldsMeta = useMemo(
    () => [
      {
        id: FilterGroupType.Project,
        fields: projectFieldsLookupByName,
      },
      {
        id: FilterGroupType.Task,
        fields: taskFieldsLookupByName,
      },
    ],
    [projectFieldsLookupByName, taskFieldsLookupByName],
  );

  const { groupedFields } = useFieldsAsGroupedFields({
    taskFields,
    projectFields,
  });
  const { defaultGroupedFields } =
    useFieldsAsDefaultGroupedFields(projectFields);

  const transformer = useMemo(
    () => getApiGroupedFilterTransformer(groups, FilterGroupType.Project),
    [groups],
  );
  const { data: widget, isLoading } = useWidgetQuery(Number(widgetId));
  const {
    selectedDashboard: { selectedId },
  } = useDashboardInfo();
  const history = useHistory();
  const onClose = () => history.push(getDashboardUrl(selectedId));
  const handleVisibleChange = (visibility: boolean) => {
    if (!visibility) {
      onClose();
    }
  };

  const [widgetForm] = Form.useForm<FormValues>();
  const [customCardForm] = Form.useForm();
  const [customChartForm] = Form.useForm<CustomChartFormValues>();
  const { mutate, isLoading: isEditSuccess } = useEditWidgetMutation();

  const handleSubmit = () => {
    if (widget) {
      switch (widget.widgetType) {
        case WidgetType.CustomCard:
          customCardForm.submit();
          break;
        case WidgetType.CustomChart:
          customChartForm.submit();
          break;
        default:
          widgetForm.submit();
          break;
      }
    }
  };

  const content = () => {
    if (widget) {
      switch (widget.widgetType) {
        case WidgetType.CustomCard:
          return (
            <EditCustomNumberWidget
              form={customCardForm}
              widget={widget as CustomCardWidget}
              fields={defaultGroupedFields}
              transformer={transformer}
              closeDrawer={setFalse}
              editWidget={mutate}
              defaultGroup={FilterGroupType.Project}
            />
          );
        case WidgetType.CustomChart:
          return (
            <EditCustomChartWidget
              form={customChartForm}
              widget={widget as CustomChartWidget}
              groupedFields={groupedFields}
              defaultGroupedFields={defaultGroupedFields}
              transformer={transformer}
              closeDrawer={setFalse}
              editWidget={mutate}
            />
          );
        default:
          return (
            <EditDefaultWidget
              form={widgetForm}
              widget={widget}
              fields={
                widget.widgetType === WidgetType.TaskByStatus
                  ? groupedFields
                  : defaultGroupedFields
              }
              transformer={transformer}
              closeDrawer={setFalse}
              editWidget={mutate}
            />
          );
      }
    }

    return null;
  };

  const actionText = useTranslation('dashboard.widget.404.action');

  return (
    <DashBoardDrawer
      className="dashboard-widget-drawer"
      large={
        widget?.widgetType === WidgetType.CustomCard ||
        widget?.widgetType === WidgetType.CustomChart
      }
      visible={visible}
      onClose={setFalse}
      title={
        <>
          <PrimaryTitle>
            <FormattedMessage id="dashboard.edit_chart" />
          </PrimaryTitle>
          <SecondaryTitle>
            <FormattedMessage
              id="dashboard.new_chart.from_type"
              values={{
                widgetTypeName: widget
                  ? widgetTypeToName(widget.widgetType)
                  : '',
              }}
            />
          </SecondaryTitle>
        </>
      }
      afterVisibleChange={handleVisibleChange}
      footer={
        widget ? (
          <ButtonGroup $reversed>
            <Button
              iconPlacement="right"
              type="primary"
              onClick={handleSubmit}
              data-cy="dashboard.edit-widget.save"
              loading={isEditSuccess}
            >
              <FormattedMessage id="save" />
            </Button>
            <Button onClick={setFalse}>
              <FormattedMessage id="cancel" />
            </Button>
          </ButtonGroup>
        ) : null
      }
    >
      {isLoading ? (
        <PageLoading />
      ) : widget ? (
        content()
      ) : (
        <NotFound actionText={actionText} linkTo={RoutePaths.dashboard} />
      )}
    </DashBoardDrawer>
  );
};
